// :cow: Cosmose CONFIDENTIAL :iso:
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CampaignStatsReport } from '@med/app/home/campaign/model/campaign-stats-report';
import { SpotPlan } from '@med/app/home/campaign/model/spot-plan.model';
import { CampaignSegmentType } from '@med/app/core/model/campaign-segment-type';
import { BrandPreference } from '@med/app/home/campaign/model/brand-preference';
import { Store } from '@ngrx/store';
import { State } from '@med/app/store';
import { Campaign } from '@med/app/home/campaign/model/campaign';
import { EstimatesDto } from '@med/app/home/campaign/model/estimates';
import { LocationUnitGroup } from '@med/app/home/campaign/model/location-unit-group';
import { HierarchicalLocationUnitGroup } from '@med/app/home/campaign/model/hierarchical-location-unit-group';
import { ConfigService } from '@med/app/core/config/config.service';
import { DateRange } from '@med/app/home/campaign/modules/report/model/date-range';
import { SpinnerService } from '@med/app/core/services/spinner.service';
import { AlertService } from '@med/app/core/services/alert.service';
import { AssetPackage } from '@med/app/home/campaign/modules/execution/model/asset.model';
import { AssetPackageType } from '@med/app/home/campaigns/models/campaignDetails';
import { CompetitorBrand } from '@med/app/shared/models/competitor-brands/competitor-brand';

@Injectable()
export class CampaignService {
  constructor(
    private http: HttpClient,
    private store: Store<State>,
    private configService: ConfigService,
    private spinnerService: SpinnerService,
    private alertService: AlertService,
    private router: Router,
  ) {
  }

  getCampaign(id?: number, params?: Params): Observable<Campaign> {
    return this.http
      .get<Campaign>(`${ this.configService.getConfig().enterpriseApi }/campaigns/${ id }`, {params})
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  putCampaign(data: Campaign): Observable<Campaign> {
    return this.http
      .put<Campaign>(`${ this.configService.getConfig().enterpriseApi }/campaigns/${ data.id }`, data, {headers: {'content-type': 'application/vnd.cosmose.campaign-update-ignore-audience-criteria+json'}})
      .pipe(
        catchError((error: Response) => {
          this.spinnerService.hide();
          this.router.navigate(['/campaign', data.id]);
          return throwError(error || 'Server error');
        }),
      );
  }

  public getEstimates(campaignId: number): Observable<EstimatesDto> {
    return this.http
      .get<any>(`${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/estimates`)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  getAssetPackages(campaignId: number): Observable<AssetPackage[]> {
    return this.http
      .get<AssetPackage[]>(`${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/asset-packages`)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  getAdFormatsRequirements(campaignId: number, params?: Params): Observable<HttpResponse<Blob>> {
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/ad-formats/requirements`;
    const options = this.getBlobRequestOptions(params);

    return this.http.get(url, options)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  public getSpotplanFile(campaignId: number, estimatedAudienceSize: number): Observable<HttpResponse<Blob>> {
    const params = {
      estimated_audience_size: estimatedAudienceSize.toString(),
    };
    const options = this.getBlobRequestOptions(params);
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/spot-plan/file`;

    return this.http.get(url, options)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  public getCampaignProposalFile(campaignId: number): Observable<HttpResponse<Blob>> {
    const options = this.getBlobRequestOptions();
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/campaign-proposal/file`;

    return this.http.get(url, options)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  public getCampaignLocationUnitsFile(campaignId: number): Observable<HttpResponse<Blob>> {
    const options = this.getBlobRequestOptions();
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/location-units/file`;

    return this.http.get(url, options)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  public getCampaignBudgetQuotationFile(campaignId: number, audienceSize: number): Observable<HttpResponse<Blob>> {
    const params = {estimated_audience_size: audienceSize.toString()};
    const options = this.getBlobRequestOptions(params);
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/budget-quotation/file`;

    return this.http.get(url, options)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  createAssetPackageBasedOnFormatType(
    campaignId: number,
    type: AssetPackageType,
    payload?: any,
  ) {
    return this.http
      .post<AssetPackage>(
        `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/asset-packages`,
        {
          type: type,
          ...payload,
        },
      )
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  getAssetPackageByFormatType(campaignId: number, types?: any) {
    return this.http
      .get<AssetPackage[]>(
        `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/asset-packages`, {params: types},
      )
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  updateAssetPackage(campaignId: number, assetPackageId: string, payload: any) {
    return this.http
      .put<AssetPackage>(
        `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/asset-packages/${ assetPackageId }`,
        payload,
      ).pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  createAsset(campaignId: number, assetPackageId: string, formData: FormData) {
    return this.http
      .post(
        `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/asset-packages/${ assetPackageId }/assets`, formData, {
          reportProgress: true,
          observe: 'events',
        },
      )
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  removeAssetFromPackage(campaignId: number, assetPackageId: string, assetFileId: string) {
    return this.http
      .delete(
        `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/asset-packages/${ assetPackageId }/assets/${ assetFileId }`,
      )
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  getAssetFromPackage(campaignId: number, assetPackageId: string, assetFileId: string) {
    return this.http
      .get(
        `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/asset-packages/${ assetPackageId }/assets/${ assetFileId }/download`,
        {responseType: 'blob'},
      )
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  removeAssetPackage(campaignId: number, assetPackage: AssetPackage) {
    return this.http
      .delete(`${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/asset-packages/${ assetPackage.id }`)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  getCompetitorsBrands(campaignId: number) {
    let headers = new HttpHeaders();
    headers = headers.append('Accept', 'application/vnd.cosmose.campaign-competitors-brand+json');
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/competitor-brands`;

    return this.http
      .get<CompetitorBrand[]>(url, {headers: headers})
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  getCampaignStats(campaignId: number, getStatsParams: { dateRange?: DateRange, audienceSegment?: string }): Observable<CampaignStatsReport[]> {
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/stats/report`;
    let params = new HttpParams();
    if (getStatsParams.dateRange != null) {
      params = params.append('dateTo', getStatsParams.dateRange.dateTo)
        .append('dateFrom', getStatsParams.dateRange.dateFrom);
    }

    if (getStatsParams.audienceSegment != null) {
      params = params.append('audienceSegment', getStatsParams.audienceSegment);
    }

    this.spinnerService.show();
    return this.http.get<CampaignStatsReport[]>(url, {params})
      .pipe(
        map(a => {
          this.spinnerService.hide();
          return a;
        }),
        catchError((error: Response) => {
          this.spinnerService.hide();
          return throwError(error || 'Server error');
        }),
      );
  }

  public getCampaignStatsByStage(campaignId: number, stage: CampaignSegmentType, getStatsParams: { dateRange?: DateRange, audienceSegment?: string }): Observable<CampaignStatsReport[]> {
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/stats/report`;

    let params = new HttpParams().append('stage', stage);
    if (getStatsParams.dateRange != null) {
      params = params.append('dateTo', getStatsParams.dateRange.dateTo)
        .append('dateFrom', getStatsParams.dateRange.dateFrom);
    }

    if (getStatsParams.audienceSegment != null) {
      params = params.append('audienceSegment', getStatsParams.audienceSegment);
    }

    return this.http.get<CampaignStatsReport[]>(url, {params})
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  public getCampaignTableColumns(campaignId: number): Observable<string[]> {
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/stats/table-columns`;
    return this.http.get<string[]>(url)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  getCampaignStatsFile(campaignId: number) {
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/stats/report/file`;
    const options = this.getBlobRequestOptions();

    return this.http.get(url, options)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  getCampaignPostBuyFile(campaignId: number) {
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/campaign-post-buy-report/file`;
    const options = this.getBlobRequestOptions();

    return this.http.get(url, options)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  getSpotPlan(campaignId: number, params?: Params): Observable<SpotPlan> {
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/spot-plan`;
    return this.http.get<SpotPlan>(url, {params: params})
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  getBrandPreferences(campaignId: number): Observable<BrandPreference[]> {
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/visitors/brand-preferences`;
    return this.http.get<BrandPreference[]>(url)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  getLocationUnitGroups(campaignId: number): Observable<LocationUnitGroup[]> {
    return this.http
      .get<LocationUnitGroup[]>(`${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/location-unit-groups`)
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  getHierarchicalLocationUnitGroups(campaignId: number): Observable<HierarchicalLocationUnitGroup[]> {
    const headers = {accept: 'application/vnd.cosmose.location-unit-groups-hierarchy+json'};
    const url = `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/location-unit-groups`;

    return this.http
      .get<HierarchicalLocationUnitGroup[]>(url, {headers})
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  uploadStats(campaignId: number, partner: string, formData: FormData) {
    return this.http
      .post(
        `${ this.configService.getConfig().enterpriseApi }/campaigns/${ campaignId }/partners/${ partner }/stats`, formData, {
          reportProgress: true,
          observe: 'events',
        },
      )
      .pipe(
        catchError((error: Response) => throwError(error || 'Server error')),
      );
  }

  private getBlobRequestOptions(params?: HttpParams | { [param: string]: string | string[] }) {
    return {
      responseType: 'blob' as const,
      observe: 'response' as const,
      params: params,
    };
  }
}
